import { mapApiPaginated } from "@eudonet/opal";
import { addOrganisationAddress as addOrganisationAddressApi, checkOrganisationDuplicates, checkOrganisationsExistByPrimaryRegistrationNumber, createOrganisation, createOrganisationAddress, deleteOrganisationAddress, fetchActivityCodesLookup, fetchLegalFormsLookup, fetchOrganisationAddresses, fetchOrganisationById, fetchOrganisations, getOrganisationPreviewById, saveOrganisationLayout, searchOrganisations as searchOrganisationsApi, setPrimaryOrganisationAddress, updateOrganisationAddress, } from "../api/organisationApi";
import { mapApiOrganisationToOrganisation } from "../mappers/mapApiOrganisationToOrganisation";
import { searchWithFallback } from "@/shared/utils/paginatedSearch";
import { withSearchRefinement } from "@/shared/utils/quickFilters";
import { mapApiAddressToAddress } from "../mappers/mapApiAddressToAddress";
import { mapAddressToApiRequest } from "../mappers/mapAddressToApiRequest";
import { pickerDateToIso } from "@/shared/utils/date-formatting";
import { isAxiosError } from "axios";
export class SaveValidationError extends Error {
    constructor(errors) {
        super("Save validation failed");
        Object.defineProperty(this, "errors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: errors
        });
        this.name = "SaveValidationError";
    }
}
export async function getOrganisations(options) {
    const apiResponse = await fetchOrganisations({
        page: options?.page,
        perPage: options?.pageSize,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder,
    });
    return mapApiPaginated(apiResponse, mapApiOrganisationToOrganisation);
}
const ORGANISATION_SEARCH_FIELDS = ["legalName"];
export async function searchOrganisations(params, signal) {
    const { search, contactId, ...query } = params;
    const filtered = Boolean(query.filterId || query.ast || query.refinement);
    // The POST search has no free-text field: with a filter active, the term travels as a refinement condition;
    // without one (e.g. the organisation autocomplete), GET /organisations?search= matches it on the legal name.
    if (search && !filtered) {
        return mapApiPaginated(await fetchOrganisations({ search, page: params.page, perPage: params.pageSize, sortBy: params.sortBy, sortOrder: params.sortOrder, contactId }, signal), mapApiOrganisationToOrganisation);
    }
    const refinement = withSearchRefinement(query.refinement, search, ORGANISATION_SEARCH_FIELDS);
    return searchWithFallback(async () => mapApiPaginated(await searchOrganisationsApi({ ...query, refinement }, signal), mapApiOrganisationToOrganisation), async () => mapApiPaginated(await fetchOrganisations({ page: params.page, perPage: params.pageSize, sortBy: params.sortBy, sortOrder: params.sortOrder }), mapApiOrganisationToOrganisation), 
    // A filter is applied (saved view, inline AST or quick filters) — never degrade to the unfiltered list.
    { allowFallback: !filtered });
}
export async function getOrganisationById(id) {
    return fetchOrganisationById(id);
}
export async function getOrganisationAddresses(id, options) {
    const apiResponse = await fetchOrganisationAddresses(id, {
        page: options?.page,
        perPage: options?.pageSize,
    });
    return mapApiPaginated(apiResponse, mapApiAddressToAddress);
}
export async function createAddress(organisationId, address) {
    return createOrganisationAddress(organisationId, mapAddressToApiRequest(address));
}
export async function updateAddress(organisationId, address) {
    await updateOrganisationAddress(organisationId, address.id, mapAddressToApiRequest(address));
}
export async function setPrimaryAddress(organisationId, addressId) {
    await setPrimaryOrganisationAddress(organisationId, addressId);
}
export async function deleteAddress(organisationId, addressId) {
    await deleteOrganisationAddress(organisationId, addressId);
}
export async function saveOrganisationById(id, state, layout, originalGenericValues = {}, logo, removeLogo) {
    const blocks = [];
    if (layout) {
        blocks.push({
            id: "main-info",
            values: {
                legalName: state.mainInformation.legalName,
                acronym: state.mainInformation.acronym,
                tradeName: state.mainInformation.tradeName,
                organisationType: state.mainInformation.organisationType,
                relationTypes: state.selectedRelationTypes,
                industrySector: state.mainInformation.industrySector,
                description: state.mainInformation.description,
            },
        });
        blocks.push({
            id: "admin-info",
            values: {
                legalForm: state.administrativeInformation.legalForm,
                establishmentNature: state.administrativeInformation.establishmentNature,
                registrationDate: pickerDateToIso(state.administrativeInformation.registrationDate),
                headcount: state.administrativeInformation.headcount,
                siret: state.administrativeInformation.siret,
                activityCode: state.administrativeInformation.activityCode,
                vatNumber: state.administrativeInformation.vatNumber,
                turnover: state.administrativeInformation.turnover,
                legalStatus: state.administrativeInformation.legalStatus,
                parentOrganisationId: state.administrativeInformation.parentOrganisationId,
            },
        });
        // OP-6251: phones, emails and socialLinks are no longer part of the global
        // save payload. Each channel has a dedicated per-channel endpoint (OP-6250).
        // Only domainName remains in the communication block.
        blocks.push({
            id: "communication",
            values: {
                domainName: state.contactInformation.domainName,
            },
        });
        for (const b of layout.layout.blocks.filter((b) => b.kind === "generic")) {
            const orig = originalGenericValues[b.id] ?? {};
            if (b.fields.some((f) => JSON.stringify(f.value ?? null) !== JSON.stringify(orig[f.key] ?? null))) {
                blocks.push({
                    id: b.id,
                    values: Object.fromEntries(b.fields.map((f) => [f.key, f.value])),
                });
            }
        }
    }
    const logoIntent = logo
        ? { action: "Set", dataUrl: logo }
        : removeLogo
            ? { action: "Remove" }
            : undefined;
    try {
        return await saveOrganisationLayout(id, {
            blocks,
            logo: logoIntent,
        });
    }
    catch (err) {
        if (isAxiosError(err) && err.response?.status === 400) {
            const errors = err.response.data?.errorResult ?? [];
            throw new SaveValidationError(errors);
        }
        throw err;
    }
}
export async function checkDuplicates(params) {
    return checkOrganisationDuplicates(params);
}
export async function getOrganisationPreview(id) {
    return getOrganisationPreviewById(id);
}
export async function checkPrimaryRegistrationNumberExistence(primaryRegistrationNumbers) {
    return checkOrganisationsExistByPrimaryRegistrationNumber(primaryRegistrationNumbers);
}
export async function createNewOrganisation(payload) {
    return createOrganisation(payload);
}
export async function addOrganisationAddress(organisationId, payload) {
    return addOrganisationAddressApi(organisationId, payload);
}
export async function getLegalFormsLookup() {
    return fetchLegalFormsLookup();
}
export async function getActivityCodesLookup() {
    return fetchActivityCodesLookup();
}
